import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  ShieldCheck,
  Search,
  Plus,
  Edit,
  Trash2,
  BarChart2,
  ChevronDown,
  ChevronUp,
  Lock,
  CheckCircle2,
  XCircle,
  History,
  RotateCcw,
  Clock,
} from "lucide-react";
import { format } from "date-fns";

import Loader from "../../loader/Loader";
import AddRestriction from "./AddRestriction";
import AuditInfoModal, { buildAuditRows } from "../../shared/AuditInfoModal";
import Pagination from "../Pagination";
import { domain } from "../../../security";
import { selectUserID } from "../../../redux/IchthusSlice";

const KNOWN_PERMISSIONS = [
  { title: "Dashboard", key: "dashboard" },
  { title: "Help & FAQ", key: "helpFaq" },
  { title: "Product Setup Access", key: "productSetup" },
  { title: "Inventory Status", key: "inventoryStatus" },
  { title: "Unit Of Measurement", key: "unitOfMeasurement" },
  { title: "Item Types", key: "itemTypes" },
  { title: "Categories", key: "categories" },
  { title: "Categories 2", key: "categories2" },
  { title: "Categories 3", key: "categories3" },
  { title: "Categories 4", key: "categories4" },
  { title: "Categories 5", key: "categories5" },
  { title: "Brands", key: "brands" },
  { title: "Pricelists", key: "pricelists" },
  { title: "Logistics Personnel", key: "logisticsPersonnel" },
  { title: "Vehicles", key: "vehicles" },
  { title: "Warehouses", key: "warehouses" },
  { title: "Terms", key: "terms" },
  { title: "Production Tagging", key: "productionTagging" },
  { title: "Procurement Access", key: "procurement" },
  { title: "Purchase Orders", key: "purchaseOrders" },
  { title: "Goods Receipts", key: "goodsReceipts" },
  { title: "Sales Access", key: "sales" },
  { title: "Sales Quotations", key: "salesQuotations" },
  { title: "All Quotation Sales", key: "allQuotationSales" }, // New
  { title: "Sales Orders", key: "salesOrders" },
  { title: "Delivery Orders", key: "deliveryOrders" },
  { title: "Production", key: "production" },
  { title: "Operations Production", key: "operationsProduction" },
  { title: "Warehouse Requests", key: "warehouseRequests" },
  { title: "Warehouse Stock", key: "warehouseStock" },
  { title: "Shipment", key: "shipment" },
  { title: "Returns Access", key: "returns" },
  { title: "Delivery Returns", key: "deliveryReturns" },
  { title: "Purchase Returns", key: "purchaseReturns" },
  { title: "Trade Returns", key: "tradeReturns" },
  { title: "Prices Access", key: "prices" },
  { title: "Selling Price Histories", key: "sellingPriceHistories" },
  { title: "Purchase Price Histories", key: "purchasePriceHistories" },
  { title: "Inventory Access", key: "inventory" },
  { title: "Inventory Dashboard", key: "inventoryDashboard" },
  { title: "Item Details", key: "itemDetails" },
  { title: "Physical Inventory", key: "physicalInventory" },
  { title: "Transfer Items", key: "transferItems" },
  { title: "Staff Access Control", key: "staffAccess" },
  { title: "Users", key: "users" },
  { title: "User Restrictions", key: "userRestriction" },
  { title: "Employees", key: "employees" },
  { title: "Approvers", key: "approvers" },
  { title: "Back Tracking Access", key: "backTracking" },
  { title: "Stock Entry (Batches)", key: "batches" },
  { title: "Serial Numbers", key: "serialNumbers" },
  { title: "Customer Credit", key: "customerCredit" },
  { title: "Pricing Display", key: "pricingDisplay" },
  { title: "Sales Order Settings", key: "salesOrderSettings" },
  { title: "Locations", key: "locations" },
  { title: "Products", key: "productList" },
  { title: "Product BOM", key: "productBom" },
  { title: "Customers", key: "customers" },
  { title: "Vendors", key: "vendors" },
  { title: "Transactions", key: "transactions" },
  { title: "POS", key: "pos" },
  { title: "Audit Logs", key: "auditLogs" },
];

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return format(new Date(dateString), "MMM dd, yyyy");
};

// ==========================================
// HISTORY MODAL
// ==========================================
const DeletedRolesModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [auditItem, setAuditItem] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${domain}/api/JobRole/history?search=${search}`,
      );
      setHistoryData(response.data);
    } catch {
      toast.error("Failed to load role history.");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = historyData.slice(indexOfFirst, indexOfLast);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <Trash2 size={18} />
            </div>
            Deleted Roles History
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        <div className="px-4 py-3 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search deleted roles..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-400 text-sm outline-none transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : historyData.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
                <History size={20} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">No deleted roles found.</p>
            </div>
          ) : (
            <>
              <div className="hidden sm:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-4 py-3">Role Name</th>
                      <th className="px-4 py-3">Deleted By</th>
                      <th className="px-4 py-3">Deleted Date</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 font-medium text-slate-800">
                          <div className="flex items-center gap-1">
                            {item.roleName}
                            <button
                              onClick={() => setAuditItem(item)}
                              className="text-amber-500 hover:text-amber-700 ml-1"
                              title="View Audit Info"
                            >
                              <Clock size={14} />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-red-500">
                          {item.deletedByFullName || "Unknown"}
                        </td>
                        <td className="px-4 py-3 text-xs text-red-400">
                          {formatDate(item.deletedDate)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              await onRestore(item.id);
                              fetchHistory();
                            }}
                            className="text-green-600 hover:text-green-700 font-semibold text-xs flex items-center gap-1 ml-auto"
                          >
                            <RotateCcw size={14} /> Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="sm:hidden flex flex-col gap-3">
                {currentItems.map((item) => (
                  <div
                    key={item.id}
                    className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-slate-800">
                        {item.roleName}
                      </span>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setAuditItem(item)}
                          className="p-2 bg-amber-50 text-amber-500 rounded-lg hover:bg-amber-100"
                        >
                          <Clock size={14} />
                        </button>
                        <button
                          onClick={async () => {
                            await onRestore(item.id);
                            fetchHistory();
                          }}
                          className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"
                        >
                          <RotateCcw size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">
                      Deleted by:{" "}
                      <span className="font-medium text-red-500">
                        {item.deletedByFullName || "Unknown"}
                      </span>{" "}
                      on {formatDate(item.deletedDate)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-3">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={historyData.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.roleName}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const UserRestriction = () => {
  const currentUserID = useSelector(selectUserID);

  const [isOpen, setIsOpen] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [jobRoles, setJobRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [auditItem, setAuditItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedRows, setExpandedRows] = useState({});
  const itemsPerPage = 10;

  const fetchRoles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${domain}/api/JobRole`);
      setJobRoles(response.data);
    } catch {
      toast.error("Failed to load roles.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const handleEdit = (role) => {
    setSelectedRole(role);
    setIsOpen(true);
  };

  const handleSoftDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this role?")) return;
    try {
      await axios.put(
        `${domain}/api/JobRole/soft-delete/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Role moved to history!");
      fetchRoles();
    } catch {
      toast.error("Failed to delete role.");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(
        `${domain}/api/JobRole/restore/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Role restored successfully!");
      fetchRoles();
    } catch {
      toast.error("Failed to restore role.");
    }
  };

  const toggleExpand = (roleId) => {
    setExpandedRows((prev) => ({ ...prev, [roleId]: !prev[roleId] }));
  };

  const filteredRoles = jobRoles.filter((role) =>
    role.roleName.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const paginatedRoles = filteredRoles.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <div className="min-h-screen pb-12">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Lock className="text-indigo-600" /> Security Roles
              </h1>
              <p className="text-sm text-slate-500 mt-1 hidden sm:block">
                Define access levels and module permissions for system roles.
              </p>
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={() => setIsHistoryModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
              >
                <History size={18} /> History
              </button>
              <button
                onClick={() => {
                  setSelectedRole(null);
                  setIsOpen(true);
                }}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl shadow-md shadow-indigo-200 transition-all"
              >
                <Plus size={18} /> Create New Role
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <BarChart2 size={24} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Configured Roles
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {filteredRoles.length}
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by role name..."
                className="w-full pl-10 pr-4 py-2 text-sm border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader />
          </div>
        ) : (
          <>
            <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="w-full text-sm text-left text-slate-600">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Role Name</th>
                    <th className="px-6 py-4 font-semibold">Access Control</th>
                    <th className="px-6 py-4 font-semibold text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paginatedRoles.map((role) => (
                    <React.Fragment key={role.id}>
                      <tr className="hover:bg-slate-50 transition">
                        <td className="px-6 py-4 font-bold text-slate-800">
                          {role.roleName}
                        </td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => toggleExpand(role.id)}
                            className="flex items-center gap-1.5 text-indigo-600 font-semibold hover:text-indigo-800 transition-colors"
                          >
                            {expandedRows[role.id] ? (
                              <ChevronUp size={16} />
                            ) : (
                              <ChevronDown size={16} />
                            )}
                            {expandedRows[role.id]
                              ? "Hide Permissions"
                              : "View Permissions"}
                          </button>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => setAuditItem(role)}
                              className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                              title="View Audit Info"
                            >
                              <Clock size={18} />
                            </button>
                            <button
                              onClick={() => handleEdit(role)}
                              className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="Edit Role"
                            >
                              <Edit size={18} />
                            </button>
                            <button
                              onClick={() => handleSoftDelete(role.id)}
                              className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Role"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        </td>
                      </tr>

                      {expandedRows[role.id] && (
                        <tr>
                          <td
                            colSpan="3"
                            className="px-6 py-6 bg-slate-50 border-y border-slate-100"
                          >
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                              {KNOWN_PERMISSIONS.map((perm) => (
                                <div
                                  key={perm.key}
                                  className="flex items-center gap-3 bg-white p-2 rounded border border-slate-200 shadow-sm"
                                >
                                  {role[perm.key] ? (
                                    <CheckCircle2
                                      size={18}
                                      className="text-emerald-500"
                                    />
                                  ) : (
                                    <XCircle
                                      size={18}
                                      className="text-slate-300"
                                    />
                                  )}
                                  <span
                                    className={`text-sm font-medium ${
                                      role[perm.key]
                                        ? "text-slate-700"
                                        : "text-slate-400 line-through"
                                    }`}
                                  >
                                    {perm.title}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {paginatedRoles.length === 0 && (
                    <tr>
                      <td
                        colSpan="3"
                        className="px-6 py-10 text-center text-slate-400"
                      >
                        No roles found matching your criteria.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-slate-100">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredRoles.length}
                currentPage={currentPage}
                paginate={setCurrentPage}
              />
            </div>

            <div className="md:hidden flex flex-col gap-3">
              {paginatedRoles.map((role) => (
                <div
                  key={role.id}
                  className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100"
                >
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                        <ShieldCheck size={18} />
                      </div>
                      <span className="font-bold text-slate-800 text-lg">
                        {role.roleName}
                      </span>
                    </div>
                    <button
                      onClick={() => toggleExpand(role.id)}
                      className="p-2 text-slate-400 hover:text-indigo-600 transition-colors"
                    >
                      {expandedRows[role.id] ? (
                        <ChevronUp size={20} />
                      ) : (
                        <ChevronDown size={20} />
                      )}
                    </button>
                  </div>

                  {expandedRows[role.id] && (
                    <div className="mt-2 mb-4 p-3 bg-slate-50 rounded-lg border border-slate-100">
                      <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                        Permissions
                      </h4>
                      <div className="grid grid-cols-2 gap-2">
                        {KNOWN_PERMISSIONS.map((perm) => (
                          <div
                            key={perm.key}
                            className="flex items-center gap-2 text-xs font-medium"
                          >
                            {role[perm.key] ? (
                              <CheckCircle2
                                size={14}
                                className="text-emerald-500"
                              />
                            ) : (
                              <XCircle size={14} className="text-slate-300" />
                            )}
                            <span
                              className={
                                role[perm.key]
                                  ? "text-slate-700"
                                  : "text-slate-400 line-through"
                              }
                            >
                              {perm.title}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-50">
                    <button
                      onClick={() => setAuditItem(role)}
                      className="text-xs font-semibold text-amber-500 hover:text-amber-700 flex items-center gap-1"
                    >
                      <Clock size={14} /> Audit
                    </button>
                    <button
                      onClick={() => handleEdit(role)}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    >
                      <Edit size={14} /> Edit
                    </button>
                    <button
                      onClick={() => handleSoftDelete(role.id)}
                      className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              ))}
              {paginatedRoles.length === 0 && (
                <div className="text-center py-10 text-slate-400 bg-white rounded-lg border border-slate-200">
                  No roles found.
                </div>
              )}
              <div className="mt-2 pb-6">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredRoles.length}
                  currentPage={currentPage}
                  paginate={setCurrentPage}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {isOpen && (
        <AddRestriction
          isOpen={isOpen}
          setIsOpen={(val) => {
            setIsOpen(val);
            if (!val) fetchRoles();
          }}
          refreshData={fetchRoles}
          jobRoleToEdit={selectedRole}
        />
      )}

      <DeletedRolesModal
        isOpen={isHistoryModalVisible}
        onClose={() => {
          setIsHistoryModalVisible(false);
          fetchRoles();
        }}
        onRestore={handleRestore}
      />

      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.roleName}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

export default UserRestriction;
